/**
 * IdleSignOut -- signs an unattended CRM session out (D2-SEC-014, #4081).
 *
 * Mounted once inside the CRM's AuthProvider. With a signed-in user and an
 * agency `idle_timeout_minutes` above 0, it treats pointer, key, wheel and
 * touch input as activity (shared across tabs, see lib/idleSignOut). One
 * minute before the timeout it asks "Still signed in?" with a countdown; when
 * the countdown runs out it runs the normal logout (refresh token blacklisted,
 * cookie cleared) and the login page explains why. Input while the prompt is
 * open does not dismiss it -- the user answers it -- and "Stay signed in" in
 * any tab clears it in every tab. Input that arrives after the timeout has
 * already passed (a laptop waking from sleep, a throttled background tab)
 * signs out rather than reviving the session.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import Modal from "../ui/Modal";
import { useAuth } from "../../contexts/auth";
import {
  ACTIVITY_EVENTS,
  ACTIVITY_WRITE_THROTTLE_MS,
  IDLE_SIGNED_OUT_STORAGE_KEY,
  LAST_ACTIVITY_STORAGE_KEY,
  idleState,
  readLastActivity,
  writeLastActivity,
} from "../../lib/idleSignOut";

const TICK_MS = 1000;

export function IdleSignOut() {
  const { user, logout } = useAuth();
  const timeoutMinutes = user?.idle_timeout_minutes ?? 0;
  const enabled = Boolean(user) && timeoutMinutes > 0;

  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  // This tab's own clock, kept alongside the shared one so a tab whose storage
  // is unavailable still times out.
  const lastActivityRef = useRef(0);
  const lastWriteRef = useRef(0);
  const promptOpenRef = useRef(false);
  const signingOutRef = useRef(false);

  const markActive = useCallback((at: number, force = false) => {
    lastActivityRef.current = at;
    if (force || at - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastWriteRef.current = at;
      writeLastActivity(at);
    }
  }, []);

  const signOut = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    sessionStorage.setItem(IDLE_SIGNED_OUT_STORAGE_KEY, String(timeoutMinutes));
    try {
      await logout();
    } finally {
      // Clearing the user sends ProtectedRoute to /login.
      signingOutRef.current = false;
    }
  }, [logout, timeoutMinutes]);

  const check = useCallback(() => {
    const now = Date.now();
    const shared = readLastActivity() ?? 0;
    const last = Math.max(lastActivityRef.current, shared);
    lastActivityRef.current = last;
    const state = idleState(last, now, timeoutMinutes);
    if (state.phase === "expired") {
      promptOpenRef.current = false;
      setSecondsLeft(null);
      void signOut();
    } else if (state.phase === "prompt") {
      promptOpenRef.current = true;
      setSecondsLeft(state.secondsLeft);
    } else {
      promptOpenRef.current = false;
      setSecondsLeft(null);
    }
  }, [signOut, timeoutMinutes]);

  useEffect(() => {
    if (!enabled) {
      promptOpenRef.current = false;
      return;
    }
    // A fresh session (login, reload, restored tab) starts its clock now, so a
    // stale timestamp left by a long-closed tab never signs the user straight out.
    markActive(Date.now(), true);

    const onActivity = () => {
      if (promptOpenRef.current) return;
      const now = Date.now();
      const last = Math.max(lastActivityRef.current, readLastActivity() ?? 0);
      // The tick may not have run (sleep, background throttling): input after
      // the timeout or inside the last minute is settled by check(), never
      // counted as activity.
      if (idleState(last, now, timeoutMinutes).phase !== "active") {
        check();
        return;
      }
      markActive(now);
    };
    const onStorage = (e: StorageEvent) => {
      // Activity (or "Stay signed in") in another tab.
      if (e.key === null || e.key === LAST_ACTIVITY_STORAGE_KEY) check();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, onActivity, {
        passive: true,
        capture: true,
      });
    }
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(check, TICK_MS);
    return () => {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, onActivity, { capture: true });
      }
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [enabled, check, markActive, timeoutMinutes]);

  const staySignedIn = useCallback(() => {
    promptOpenRef.current = false;
    setSecondsLeft(null);
    markActive(Date.now(), true);
  }, [markActive]);

  if (!enabled || secondsLeft === null) return null;

  return (
    <Modal
      onClose={staySignedIn}
      title="Still signed in?"
      titleIcon={<Clock className="h-5 w-5 text-[var(--color-warning)]" />}
      size="sm"
      closeOnBackdrop={false}
      feedbackTrigger={false}
      // Above every toast and nudge (SectionNudge is z-[9000]): a sign-out
      // warning must never be hidden.
      zIndexClass="z-[9100]"
      footer={
        <>
          <button
            type="button"
            onClick={() => void signOut()}
            className="min-h-[44px] rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-page)]"
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={staySignedIn}
            className="min-h-[44px] rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-accent-hover)]"
          >
            Stay signed in
          </button>
        </>
      }
    >
      <p
        className="px-5 sm:px-6 py-4 text-sm text-[var(--color-text-secondary)]"
        role="timer"
        aria-live="polite"
      >
        You have not used the CRM for a while. For your security you will be
        signed out in{" "}
        <span className="font-semibold text-[var(--color-text-primary)]">
          {secondsLeft} {secondsLeft === 1 ? "second" : "seconds"}
        </span>
        .
      </p>
    </Modal>
  );
}
